import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type { FleetPublishEntry } from '../../api/config';

// Standalone SVG sparkline. No data fetching, no layout coupling — pure
// props in, pixels out. Will land in future location-detail and per-device
// surfaces. Bucket-grid alignment with EventVolumeChart is structural: both
// consume the same `bucketCount` and `bucketWidth` so columns line up
// pixel-for-pixel without coordinated axis machinery.

const DEFAULT_BUCKET_COUNT = 96; // 24h at 15-min buckets
const DEFAULT_BUCKET_SIZE_MINUTES = 15;

export interface HeartbeatSparklineProps {
  publishes: FleetPublishEntry[];
  /** Width of each column, including gap. Default 4 px. */
  bucketWidth?: number;
  /** Px between columns. Default 1 px. */
  gap?: number;
  /** Total chart height in px. Default 36. */
  height?: number;
  /** Number of buckets to display (rolling window). Default 96. With
      bucketSizeMinutes=15 → 24h; with bucketSizeMinutes=60 → 24h at 24 cols. */
  bucketCount?: number;
  /** Width of each chart bucket in minutes. The API ships 15-min entries; if
      this is larger than 15, sub-buckets are aggregated as MAX(seen) / MIN(good)
      so the cell shows the worst-case spread within the window. Default 15. */
  bucketSizeMinutes?: number;
  /** "Now" — defaults to Date.now(). Most recent CLOSED bucket = floor(now / bucketSize) - 1. */
  nowMs?: number;
  /** Beacons-good shading color (lower portion of bar). */
  goodColor?: string;
  /** Beacons-weak shading color (upper portion of bar). */
  weakColor?: string;
  /** Blackout (no readings) stripe pattern color. */
  blackoutColor?: string;
}

interface BucketCell {
  startMs: number;
  seen: number;
  good: number;
  isBlackout: boolean;
}

function buildBuckets(
  publishes: FleetPublishEntry[],
  nowMs: number,
  count: number,
  bucketSizeMs: number,
): BucketCell[] {
  const currentBucketStart = Math.floor(nowMs / bucketSizeMs) * bucketSizeMs;
  // Most recent CLOSED bucket — the in-progress bucket is intentionally not rendered
  // (see plan: avoids the always-shorter trailing cell that reads as degradation).
  const lastClosedBucketStart = currentBucketStart - bucketSizeMs;
  const firstBucketStart = lastClosedBucketStart - (count - 1) * bucketSizeMs;

  // Aggregate API 15-min entries into chart buckets. When bucketSize == 15,
  // each chart bucket has exactly one source entry. When larger (e.g. 60),
  // multiple sub-buckets map to the same chart bucket:
  //   • `seen` is rendered as the MAX across constituent sub-buckets — the
  //     peak total beacon count in the hour, so the bar's top edge shows
  //     "most beacons heard at any moment."
  //   • `good` is rendered as the MIN across constituent sub-buckets — the
  //     worst good count in the hour, so the dark portion shows "fewest
  //     good beacons at any moment."
  // Together they render a worst-case combined view: top = peak heard,
  // bottom dark = lowest good, weak shading fills the spread between. A
  // bad quarter (or peak quarter with low good) immediately surfaces.
  // Missing sub-buckets do not contribute; if all are missing the cell
  // renders as blackout.
  type Accumulator = { seenMax: number; goodMin: number };
  const acc = new Map<number, Accumulator>();
  for (const entry of publishes) {
    const ms = Date.parse(entry[0]);
    if (Number.isNaN(ms)) continue;
    const bucketStart = Math.floor(ms / bucketSizeMs) * bucketSizeMs;
    const existing = acc.get(bucketStart);
    if (existing) {
      existing.seenMax = Math.max(existing.seenMax, entry[1]);
      existing.goodMin = Math.min(existing.goodMin, entry[2]);
    } else {
      acc.set(bucketStart, { seenMax: entry[1], goodMin: entry[2] });
    }
  }

  const cells: BucketCell[] = [];
  for (let i = 0; i < count; i++) {
    const startMs = firstBucketStart + i * bucketSizeMs;
    const entry = acc.get(startMs);
    if (entry) {
      const seen = entry.seenMax;
      const good = entry.goodMin;
      cells.push({ startMs, seen, good, isBlackout: seen === 0 });
    } else {
      cells.push({ startMs, seen: 0, good: 0, isBlackout: true });
    }
  }
  return cells;
}

export const HeartbeatSparkline: React.FC<HeartbeatSparklineProps> = ({
  publishes,
  bucketWidth = 4,
  gap = 1,
  height = 36,
  bucketCount = DEFAULT_BUCKET_COUNT,
  bucketSizeMinutes = DEFAULT_BUCKET_SIZE_MINUTES,
  nowMs,
  goodColor = '#2e7d32',
  // weak (RSSI ≤ -85 dBm) reads yellow, not light-green: a beacon hovering
  // at the edge of usable signal is a "needs attention" state, not a softer
  // version of healthy. Matches the yellow used for radio churn on the
  // volume chart so the eye treats the two warnings as the same severity.
  weakColor = '#f9a825',
  blackoutColor = '#c62828',
}) => {
  const effectiveNow = nowMs ?? Date.now();
  const bucketSizeMs = bucketSizeMinutes * 60 * 1000;
  const cells = React.useMemo(
    () => buildBuckets(publishes, effectiveNow, bucketCount, bucketSizeMs),
    [publishes, effectiveNow, bucketCount, bucketSizeMs]
  );

  // Fixed-scale Y axis: peak height of the tallest bar in the window.
  // Empty / all-blackout window degrades gracefully — bars show full-height
  // blackout pattern at scale=1 (everything will read as silent anyway).
  const peakSeen = React.useMemo(() => {
    let p = 0;
    for (const c of cells) if (c.seen > p) p = c.seen;
    return Math.max(p, 1);
  }, [cells]);

  const totalWidth = bucketCount * bucketWidth + (bucketCount - 1) * gap;
  const blackoutPatternId = React.useId();

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${totalWidth} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Heartbeat — distinct beacons seen per bucket"
      style={{ display: 'block' }}
    >
      <defs>
        <pattern
          id={blackoutPatternId}
          width="3"
          height="3"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="3" height="3" fill={blackoutColor} fillOpacity={0.2} />
          <line x1="0" y1="0" x2="0" y2="3" stroke={blackoutColor} strokeWidth="1.5" />
        </pattern>
      </defs>

      {cells.map((cell, idx) => {
        const x = idx * (bucketWidth + gap);
        const tooltip = (
          <HeartbeatBucketTooltip
            cell={cell}
            bucketSizeMs={bucketSizeMs}
            goodColor={goodColor}
            weakColor={weakColor}
            blackoutColor={blackoutColor}
          />
        );
        if (cell.isBlackout) {
          return (
            <Tooltip key={cell.startMs} title={tooltip} arrow disableInteractive>
              <g>
                {/* Invisible hit-area rect — full column height so the tooltip
                    triggers anywhere over the bucket, not just the rendered bar. */}
                <rect x={x} y={0} width={bucketWidth} height={height} fill="transparent" />
                <rect
                  x={x}
                  y={0}
                  width={bucketWidth}
                  height={height}
                  fill={`url(#${blackoutPatternId})`}
                />
              </g>
            </Tooltip>
          );
        }
        const totalH = (cell.seen / peakSeen) * height;
        const goodH = (cell.good / peakSeen) * height;
        const weakH = totalH - goodH;
        // Bar grows up from the baseline at y=height. Stacking: WEAK sits at
        // the bottom (touching the baseline), GOOD on top — the bar's top
        // edge represents total beacons seen and the dark portion is what's
        // good, so a tile getting weaker reads as "good ceiling sinking."
        const weakY = height - weakH;
        const goodY = height - totalH;
        return (
          <Tooltip key={cell.startMs} title={tooltip} arrow disableInteractive>
            <g>
              <rect x={x} y={0} width={bucketWidth} height={height} fill="transparent" />
              {weakH > 0 && (
                <rect x={x} y={weakY} width={bucketWidth} height={weakH} fill={weakColor} />
              )}
              <rect x={x} y={goodY} width={bucketWidth} height={goodH} fill={goodColor} />
            </g>
          </Tooltip>
        );
      })}
    </svg>
  );
};

// Bucket-window label like "12:00 – 13:00".
function formatBucketRange(startMs: number, bucketSizeMs: number): string {
  const start = new Date(startMs);
  const end = new Date(startMs + bucketSizeMs);
  const fmt = (d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${fmt(start)} – ${fmt(end)}`;
}

interface SwatchRowProps {
  color: string;
  label: string;
  value: number | string;
}

const SwatchRow: React.FC<SwatchRowProps> = ({ color, label, value }) => (
  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minHeight: 16 }}>
    <Box
      sx={{
        width: 8,
        height: 8,
        bgcolor: color,
        borderRadius: '50%',
        flexShrink: 0,
        border: '1px solid rgba(255,255,255,0.4)',
      }}
    />
    <Typography variant="caption" sx={{ flex: 1, mr: 1 }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Stack>
);

interface HeartbeatBucketTooltipProps {
  cell: BucketCell;
  bucketSizeMs: number;
  goodColor: string;
  weakColor: string;
  blackoutColor: string;
}

const HeartbeatBucketTooltip: React.FC<HeartbeatBucketTooltipProps> = ({
  cell,
  bucketSizeMs,
  goodColor,
  weakColor,
  blackoutColor,
}) => {
  if (cell.isBlackout) {
    return (
      <Box sx={{ minWidth: 160 }}>
        <SwatchRow color={blackoutColor} label="No readings" value="—" />
        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
          {formatBucketRange(cell.startMs, bucketSizeMs)}
        </Typography>
      </Box>
    );
  }
  // Cell values are MAX(seen) / MIN(good) across constituent sub-buckets —
  // they describe the worst-case spread within the window, so the weak
  // count below is the spread between the two extremes, not a literal
  // count from any single sub-bucket.
  const weak = cell.seen - cell.good;
  return (
    <Box sx={{ minWidth: 160 }}>
      <SwatchRow color={goodColor} label="Strong (> -85 dBm)" value={cell.good} />
      <SwatchRow color={weakColor} label="Weak (≤ -85 dBm)" value={weak} />
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
        {formatBucketRange(cell.startMs, bucketSizeMs)}
      </Typography>
    </Box>
  );
};
